import React, { useEffect, useState } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useFetchAuthTagById from "../../hooks/useFetchAuthTagById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { IconBtnEditBase } from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import { useAuth } from "../../contexts/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const ViewAuthTag: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();
  const { id: authTagId } = useParams();

  // Permission checks for auth tag actions
  const canEditAuthTag = hasPermission("auth_tag", "edit");
  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    created_by_user: null,
    modified_by_user: null,
    create_date: "",
    update_date: "",
    is_active: true,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch tag data
  const [authTagData] = useFetchAuthTagById({
    setIsLoading,
    authTagId: authTagId || null,
  });

  useEffect(() => {
    if (authTagData) {
      setFormData({
        name: authTagData?.name,
        description: authTagData?.description,
        created_by_user: authTagData?.created_by_user,
        modified_by_user: authTagData?.modified_by_user,
        create_date: authTagData?.create_date,
        update_date: authTagData?.update_date,
        is_active: authTagData?.is_active,
      });
      setCurrentBreadcrumbPage({
        name: authTagData?.name,
        id: authTagData?.id,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [authTagData, setCurrentBreadcrumbPage]);

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <Box className="text-box-card-white">
          <Box className="text-box-header">
            <h3>{t("authTags.view.header")}</h3>
            {canEditAuthTag && (
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/tags/edit/${authTagId}`)}
              >
                <IconBtnEditBase
                  width={32}
                  height={32}
                  ariaLabel={t("authTags.view.ariaEdit")}
                />
              </button>
            )}
          </Box>

          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("authTags.view.label.tagName")}
              </label>
              <TextField
                type="text"
                name="name"
                value={formData?.name}
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("authTags.view.label.description")}
              </label>
              <TextField
                type="text"
                name="description"
                value={
                  formData?.description || t("authTags.view.noDescription")
                }
                className="form-control read-only"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("authTags.view.label.status")}
              </label>
              <TextField
                type="text"
                name="is_active"
                value={
                  formData?.is_active
                    ? t("common.status.active")
                    : t("common.status.inactive")
                }
                className="form-control read-only"
                disabled
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default ViewAuthTag;
